import { useRef, useState } from 'react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import type { DocumentEditOperationDTO, DocumentThemePresetDTO } from '@shared/schemas/document-model';
import { Artifact } from '@/components/ai-elements/artifact';
import { getChatErrorMessage } from '../chat-errors';
import { parseCodegenGenerationId } from '../codegen-document';
import { useDocumentGenerationStatus } from '../hooks/useDocumentGeneration';
import { CanvasBanners } from './CanvasBanners';
import { CanvasHeader } from './CanvasHeader';
import { CanvasLoadError, CanvasPlaceholder } from './CanvasPlaceholder';
import { CanvasPreviewFrame, type ScrollRequest } from './CanvasPreviewFrame';
import { buildThemeOperation } from './edit-operations';
import { hasGenerationPlan, resolveCanvasPlaceholderLabel } from './generation-progress';
import { isNotReadyError, useCanvasEdits, useGenerationModel, useGenerationPreview } from './use-canvas-queries';

type DocumentCanvasProps = {
  conversationId: string;
  document: ChatDocumentDTO;
  onClose: () => void;
};

type CanvasState = {
  familyId: string | undefined;
  generationId: string;
  /** Versions affichées avant celle-ci : permet de « revenir » si une édition échoue. */
  history: string[];
  revisionNumber: number | undefined;
  source: ChatDocumentDTO;
};

function initialState(document: ChatDocumentDTO): CanvasState {
  return {
    familyId: undefined,
    generationId: parseCodegenGenerationId(document) ?? '',
    history: [],
    revisionNumber: undefined,
    source: document,
  };
}

/**
 * Canvas d'un document du moteur `blocks` : aperçu HTML en direct (iframe sandboxée),
 * édition par opérations sur le modèle, versions, export et thème.
 */
export function DocumentCanvas({ conversationId, document, onClose }: DocumentCanvasProps) {
  const [state, setState] = useState(() => initialState(document));
  const [scrollRequest, setScrollRequest] = useState<ScrollRequest | null>(null);
  const pendingScrollSectionRef = useRef<string | undefined>(undefined);

  if (state.source !== document) {
    // L'utilisateur a rouvert un document depuis le chat : on repart de cette version.
    setState(initialState(document));
    setScrollRequest(null);
  }

  const { generationId } = state;
  const statusQuery = useDocumentGenerationStatus(conversationId, generationId);
  const status = statusQuery.data;
  const progress = status?.progress;
  const isSucceeded = status?.status === 'succeeded';
  const isInProgress = status?.status === 'pending' || status?.status === 'generating';
  const planReady = isSucceeded || hasGenerationPlan(progress);
  // Une version terminée est immuable : on bascule sur son rendu final une fois pour toutes.
  const revision = isSucceeded || !progress ? undefined : `${progress.modelRevision}:${progress.stage}`;
  const familyId = state.familyId ?? (status?.status === 'succeeded' ? status.result.generatedDocumentId : undefined);
  const versionNumber = status?.status === 'succeeded' ? status.result.revisionNumber : state.revisionNumber;

  const previewQuery = useGenerationPreview({ conversationId, enabled: planReady, generationId, revision });
  const modelQuery = useGenerationModel({ conversationId, enabled: planReady, generationId, revision });
  const model = modelQuery.data?.model ?? null;

  const edits = useCanvasEdits({
    baseGenerationId: generationId,
    conversationId,
    documentId: familyId,
    onAccepted: (accepted) => {
      const sectionId = pendingScrollSectionRef.current;
      pendingScrollSectionRef.current = undefined;
      setState((current) => ({
        ...current,
        familyId: accepted.generatedDocumentId ?? familyId,
        generationId: accepted.generationId,
        history: [...current.history, current.generationId],
        revisionNumber: accepted.revisionNumber,
      }));
      setScrollRequest(sectionId ? { nonce: (scrollRequest?.nonce ?? 0) + 1, sectionId } : null);
    },
  });
  const isSaving = edits.isPending;

  function applyOperations(operations: DocumentEditOperationDTO[], scrollToSectionId?: string) {
    if (!isSucceeded || !familyId || isSaving) {
      return;
    }
    pendingScrollSectionRef.current = scrollToSectionId;
    edits.mutate(operations);
  }

  function showVersion(next: { generationId: string; revisionNumber?: number }) {
    if (next.generationId === generationId) {
      return;
    }
    setScrollRequest(null);
    setState((current) => ({
      ...current,
      generationId: next.generationId,
      history: [...current.history, current.generationId],
      revisionNumber: next.revisionNumber,
    }));
  }

  function revertToPreviousVersion() {
    setState((current) => {
      const previous = current.history[current.history.length - 1];
      return previous
        ? { ...current, generationId: previous, history: current.history.slice(0, -1), revisionNumber: undefined }
        : current;
    });
  }

  const html = previewQuery.data;
  let body;
  if (html) {
    body = (
      <CanvasPreviewFrame
        canEdit={isSucceeded && Boolean(familyId) && Boolean(model)}
        html={html}
        isSaving={isSaving}
        model={model}
        onOperations={applyOperations}
        scrollRequest={scrollRequest}
        title={document.title}
      />
    );
  } else if (statusQuery.isError && !status) {
    body = <CanvasLoadError message={getChatErrorMessage(statusQuery.error)} onRetry={() => void statusQuery.restartPolling()} />;
  } else if (planReady && previewQuery.isError && !(isInProgress && isNotReadyError(previewQuery.error))) {
    body = <CanvasLoadError message={getChatErrorMessage(previewQuery.error)} onRetry={() => void previewQuery.refetch()} />;
  } else {
    body = (
      <CanvasPlaceholder
        label={isInProgress ? resolveCanvasPlaceholderLabel(progress) : 'Chargement de l’aperçu…'}
      />
    );
  }

  return (
    <Artifact className="h-full rounded-none border-none bg-sidebar shadow-none" data-testid="document-canvas">
      <CanvasHeader
        conversationId={conversationId}
        currentGenerationId={generationId}
        document={document}
        familyId={familyId}
        isSaving={isSaving}
        isSucceeded={isSucceeded}
        kind={model?.kind ?? null}
        onClose={onClose}
        onOpenVersion={showVersion}
        onThemeChange={(preset: DocumentThemePresetDTO) => {
          if (model) {
            applyOperations([buildThemeOperation(model.theme, preset)]);
          }
        }}
        statusLabel={isInProgress ? 'Génération en cours' : null}
        themePreset={model?.theme.preset ?? null}
        title={document.title}
        versionNumber={versionNumber}
      />
      <CanvasBanners
        generationId={generationId}
        onRetryPolling={() => void statusQuery.restartPolling()}
        onRevert={state.history.length > 0 ? revertToPreviousVersion : null}
        pollingExhausted={statusQuery.pollingExhausted}
        progress={progress}
        status={status}
      />
      <div className="min-h-0 flex-1">{body}</div>
    </Artifact>
  );
}
